'use client';

import { useQuery } from '@tanstack/react-query';
import type { CoPilotAgentMessagesDebugResponse } from '@terra/shared-dto-chat-types';
import { getCurrentGatewayUrl } from '../../utils/environmentDetection';
import { getAuthHeaders } from '../../utils/authHeaders';

export const CHAT_AGENT_MESSAGES_QUERY_KEY = 'chat-agent-messages';

const buildAgentMessagesUrl = ({
  chatId,
  applicationId,
}: {
  chatId: string;
  applicationId: string;
}): string =>
  `${getCurrentGatewayUrl()}/chat/graphs/co-pilot/${chatId}/agent-messages?applicationId=${encodeURIComponent(applicationId)}`;

export function useChatAgentMessages({
  chatId,
  applicationId,
  enabled = true,
  refetchWhileStreaming = false,
}: {
  chatId: string;
  applicationId?: string;
  enabled?: boolean;
  refetchWhileStreaming?: boolean;
}) {
  return useQuery({
    enabled: enabled && Boolean(chatId) && Boolean(applicationId),
    queryKey: [CHAT_AGENT_MESSAGES_QUERY_KEY, chatId, applicationId],
    queryFn: async (): Promise<CoPilotAgentMessagesDebugResponse> => {
      if (!applicationId) {
        throw new Error('Missing applicationId for agent messages');
      }

      const authHeaders = await getAuthHeaders();
      const response = await fetch(
        buildAgentMessagesUrl({ chatId, applicationId }),
        {
          credentials: 'include',
          headers: { ...authHeaders },
          method: 'GET',
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to load agent messages (${response.status})`);
      }

      return (await response.json()) as CoPilotAgentMessagesDebugResponse;
    },
    refetchInterval: refetchWhileStreaming ? 2000 : false,
    staleTime: 1000,
  });
}
